import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { categories, contrastCases, type Category } from "@/mocks/chiGuaData";

const tagColor: Record<string, string> = {
  persona: "bg-primary-100 text-primary-800",
  filter: "bg-accent-100 text-accent-900",
  flip: "bg-secondary-100 text-secondary-900",
  crash: "bg-foreground-950 text-background-50",
  idol: "bg-primary-500 text-background-50",
};

const tagName = (id: string) => categories.find((c) => c.id === id)?.name || id;

function CaseCard({ item }: { item: (typeof contrastCases)[number] }) {
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/60">
      <div className="flex items-center justify-between gap-2 px-5 pt-4">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            tagColor[item.tagId] || tagColor.crash
          }`}
        >
          <i className="ri-price-tag-3-line" />
          {tagName(item.tagId)}
        </span>
        <span className="whitespace-nowrap text-[11px] font-medium text-foreground-500">
          {item.date}
        </span>
      </div>

      <h3 className="px-5 pt-3 font-heading text-lg font-black leading-snug text-foreground-950 md:text-xl">
        {item.title}
      </h3>
      <p className="px-5 pt-1.5 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

      {/* 前后对照 */}
      <div className="mt-4 px-5">
        <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-background-200/80 sm:grid-cols-2">
          <div className="bg-secondary-50 p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-700">
              <span className="h-2 w-2 rounded-full bg-secondary-500" />
              立人设 · 台前
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-800">{item.before}</p>
          </div>
          <div className="relative bg-primary-100/60 p-4">
            <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-background-50/70 text-primary-600">
              <i className="ri-swap-fill" />
            </span>
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-700">
              <span className="h-2 w-2 rounded-full bg-primary-500" />
              翻车后 · 实况
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-800">{item.after}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 border-t border-dashed border-background-300/70 px-5 py-3.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
          <i className="ri-emotion-sad-line" />
          反差结论：{item.verdict}
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-foreground-500">
          <i className="ri-fire-fill text-accent-500" />
          {Math.round(item.heat / 1000)}k 围观
        </span>
      </div>
    </article>
  );
}

export default function ContrastSection() {
  const [active, setActive] = useState("all");

  const list = useMemo(
    () => (active === "all" ? contrastCases : contrastCases.filter((c) => c.tagId === active)),
    [active],
  );

  const renderChip = (c: Category) => (
    <button
      key={c.id}
      type="button"
      onClick={() => setActive(c.id)}
      className={`group flex w-[200px] flex-none flex-col overflow-hidden rounded-xl border text-left transition-all ${
        active === c.id
          ? "border-primary-500 ring-2 ring-primary-300"
          : "border-background-200/70 hover:border-primary-300"
      }`}
    >
      <div className="relative h-24 w-full overflow-hidden">
        <img
          src={c.image}
          alt={c.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-full bg-foreground-950/80 px-2 py-0.5 text-[10px] font-semibold text-background-50">
          {c.heat}
        </span>
      </div>
      <div className="flex items-center justify-between bg-background-100 px-3 py-2">
        <span className="text-sm font-bold text-foreground-900">{c.short}</span>
        <i
          className={`ri-add-line text-xs ${active === c.id ? "text-primary-500" : "text-foreground-400"}`}
        />
      </div>
    </button>
  );

  return (
    <section id="contrast" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          index="02"
          en="The Contrast Field"
          title="反差现场 · 逐帧对照"
          desc="选中任一栏目或点「全部」，把翻车前后摆在同一张卡片上慢慢看。左侧是台前的立人设口径，右侧是翻车后的实况物料。"
        />

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setActive("all")}
            className={`flex-none rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              active === "all"
                ? "bg-foreground-950 text-background-50"
                : "bg-background-50 text-foreground-700 hover:bg-background-200/60"
            }`}
          >
            全部 {contrastCases.length}
          </button>
          <span className="hidden h-px flex-1 bg-background-300/70 sm:block" />
        </div>

        <div className="mt-4 flex gap-4 overflow-x-auto pb-3 lg:grid lg:grid-cols-5 lg:overflow-visible">
          {categories.map(renderChip)}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {list.map((item) => (
            <CaseCard key={item.id} item={item} />
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-6 rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-10 text-center text-sm text-foreground-500">
            这个栏目暂时没有公开可查证的新物料，先去别的栏目刷一刷吧。
          </div>
        )}

        <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-primary-500" />
          以上均为基于公开访谈、通稿、节目与可查证路透的并排还原，不包含私生活主观猜测；如涉及争议我们会在核实后修正或下线。
        </p>
      </div>
    </section>
  );
}